/**
 * Deposits — portfolio analytics across all banks (PHASE2_SPEC §4.3).
 *
 * Layout:
 *   Filter bar (date + bank multi-select)
 *   KPI tiles (deposits ₹Cr · accounts · CASA ₹Cr · CASA % · FD ₹Cr)
 *   Donut — deposit mix
 *   Top 20 deposit products (clickable → /products/:code?type=DEPOSIT)
 *   Per-bank table (clickable → /banks/:orgId)
 *   Per-branch table — only when ≥ 1 bank is filtered (clickable → branch detail)
 *
 * Filter state lives in URL search params so back/forward and bookmarking
 * restore the view exactly.
 */

import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AnalyticsFilterBar from '../../components/AnalyticsFilterBar';
import BankBadge from '../../components/BankBadge';
import Card from '../../components/Card';
import Donut, { DonutLegend, type DonutDatum } from '../../components/Donut';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import { formatDate } from '../../helpers/dateFormat';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { DEPOSIT_COLORS } from '../../helpers/palette';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchDeposits, selectDeposits } from '../../redux/slices/dashboard';
import { fetchBanks, selectBanks } from '../../redux/slices/banks';
import type {
	DepositByBankRow,
	DepositByBranchRow,
	DepositProductRow,
} from '../../type/api';

const MIX_COLOR: Record<string, string> = {
	Savings: DEPOSIT_COLORS.savings,
	FD: DEPOSIT_COLORS.fd,
	RD: DEPOSIT_COLORS.rd,
	Current: DEPOSIT_COLORS.current,
};

function parseOrgIds(raw: string | null): number[] {
	if (!raw) return [];
	return raw
		.split(',')
		.map((s) => parseInt(s, 10))
		.filter((n) => Number.isFinite(n) && n > 0);
}

export default function DepositsPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectDeposits);
	const banks = useAppSelector(selectBanks);

	const [params, setParams] = useSearchParams();
	const date = params.get('date');
	const orgIds = useMemo(() => parseOrgIds(params.get('orgIds')), [params]);

	useEffect(() => {
		if (banks.length === 0) dispatch(fetchBanks());
	}, [banks.length, dispatch]);

	useEffect(() => {
		dispatch(
			fetchDeposits({ date: date ?? undefined, orgIds: orgIds.length ? orgIds : undefined }),
		);
	}, [dispatch, date, params]); // eslint-disable-line react-hooks/exhaustive-deps

	const data = cell.data;
	const products: DepositProductRow[] = useMemo(
		() => (Array.isArray(data?.topProducts) ? data!.topProducts : []),
		[data],
	);
	const byBank: DepositByBankRow[] = useMemo(
		() => (Array.isArray(data?.byBank) ? data!.byBank : []),
		[data],
	);
	const byBranch: DepositByBranchRow[] = useMemo(
		() => (Array.isArray(data?.byBranch) ? data!.byBranch : []),
		[data],
	);

	const productSort = useSortable<DepositProductRow>(products, {
		field: 'balanceCr',
		dir: 'desc',
	});
	const bankSort = useSortable<DepositByBankRow>(byBank, { field: 'depositCr', dir: 'desc' });
	const branchSort = useSortable<DepositByBranchRow>(byBranch, {
		field: 'depositCr',
		dir: 'desc',
	});

	const setDate = (next: string) => {
		const p = new URLSearchParams(params);
		if (next) p.set('date', next);
		else p.delete('date');
		setParams(p, { replace: true });
	};
	const setOrgIds = (ids: number[]) => {
		const p = new URLSearchParams(params);
		if (ids.length) p.set('orgIds', ids.join(','));
		else p.delete('orgIds');
		setParams(p, { replace: true });
	};

	const handleRetry = () =>
		dispatch(
			fetchDeposits({ date: date ?? undefined, orgIds: orgIds.length ? orgIds : undefined }),
		);

	const isLoading = cell.status === 'loading';

	const filterBar = (
		<AnalyticsFilterBar
			date={date ?? data?.asOf ?? null}
			availableDates={data?.snapshotsAvailable}
			onDateChange={setDate}
			banks={banks}
			selectedOrgIds={orgIds}
			onOrgIdsChange={setOrgIds}
			onRefresh={handleRetry}
			loading={isLoading}
		/>
	);

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Deposits</h1>
					</div>
				</div>
				{filterBar}
				<Card>
					<LoadingSkeleton rows={6} columns={6} />
				</Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Deposits</h1>
					</div>
				</div>
				{filterBar}
				<ErrorBanner message={cell.error || 'Failed to load deposits'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Deposits</h1>
					</div>
				</div>
				{filterBar}
				<Card>
					<EmptyState
						title="No deposit data"
						hint="Once the ETL completes a snapshot, this page will populate."
					/>
				</Card>
			</div>
		);
	}

	const summary = data.summary;
	const mix = Array.isArray(data.mix) ? data.mix : [];
	const mixData: DonutDatum[] = mix.map((m) => ({
		name: m.category,
		value: Math.abs(m.balanceCr),
		color: MIX_COLOR[m.category] || 'var(--navy-500)',
	}));

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Deposits</h1>
					<div className="page-sub">
						Deposit portfolio across all banks.
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
			</div>

			{filterBar}

			<div className="grid cols-5">
				<KpiTile label="Total Deposits" value={formatCrore(summary.totalDepositCr, 2)} />
				<KpiTile label="Accounts" value={formatCount(summary.totalDepositCount)} />
				<KpiTile label="CASA ₹Cr" value={formatCrore(summary.casaCr, 2)} />
				<KpiTile label="CASA %" value={formatPct(summary.casaPct)} primary />
				<KpiTile
					label="FD ₹Cr"
					value={formatCrore(summary.fdCr, 2)}
					sub={`${formatPct(summary.fdShare)} share`}
				/>
			</div>

			<Card title="Deposit mix">
				<div
					style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: 18, alignItems: 'center' }}
				>
					<Donut data={mixData} />
					<DonutLegend data={mixData} />
				</div>
			</Card>

			<Card title={`Top deposit products (${products.length})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="productCode" sort={productSort}>
									Code
								</SortableTh>
								<SortableTh field="productName" sort={productSort}>
									Product
								</SortableTh>
								<SortableTh field="balanceCr" sort={productSort} numeric>
									Balance ₹Cr
								</SortableTh>
								<SortableTh field="count" sort={productSort} numeric>
									Accounts
								</SortableTh>
							</tr>
						</thead>
						<tbody>
							{productSort.rows.map((p) => (
								<tr
									key={p.productCode}
									className="clickable"
									onClick={() =>
										navigate(`/products/${encodeURIComponent(p.productCode)}?type=DEPOSIT`)
									}
									tabIndex={0}
								>
									<td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
										{p.productCode}
									</td>
									<td>{p.productName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(p.balanceCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(p.count)}
									</td>
								</tr>
							))}
							{!productSort.rows.length && (
								<tr>
									<td colSpan={4}>
										<EmptyState title="No products" />
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>

			<Card title={`Per-bank breakdown (${byBank.length})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgId" sort={bankSort} numeric>
									ID
								</SortableTh>
								<SortableTh field="orgName" sort={bankSort}>
									Bank
								</SortableTh>
								<SortableTh field="depositCr" sort={bankSort} numeric>
									Deposit ₹Cr
								</SortableTh>
								<SortableTh field="depositCount" sort={bankSort} numeric>
									Accounts
								</SortableTh>
								<SortableTh field="casaPct" sort={bankSort} numeric>
									CASA %
								</SortableTh>
							</tr>
						</thead>
						<tbody>
							{bankSort.rows.map((b) => (
								<tr
									key={b.orgId}
									className="clickable"
									onClick={() => navigate(`/banks/${b.orgId}`)}
									tabIndex={0}
								>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{b.orgId}
									</td>
									<td>
										<BankBadge orgId={b.orgId} orgName={b.orgName} />
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(b.depositCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(b.depositCount)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatPct(b.casaPct)}
									</td>
								</tr>
							))}
							{!bankSort.rows.length && (
								<tr>
									<td colSpan={5}>
										<EmptyState title="No banks" />
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>

			{orgIds.length > 0 && (
				<Card
					title={`Per-branch breakdown (${byBranch.length}${
						byBranch.length >= 500 ? ', capped at 500' : ''
					})`}
					tools={
						<span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
							Drill into a branch
						</span>
					}
				>
					<div style={{ overflowX: 'auto' }}>
						<table className="tbl" style={{ width: '100%' }}>
							<thead>
								<tr>
									<SortableTh field="orgName" sort={branchSort}>
										Bank
									</SortableTh>
									<SortableTh field="branchIdLocal" sort={branchSort}>
										Branch ID
									</SortableTh>
									<SortableTh field="branchName" sort={branchSort}>
										Branch
									</SortableTh>
									<SortableTh field="depositCr" sort={branchSort} numeric>
										Deposit ₹Cr
									</SortableTh>
									<SortableTh field="depositCount" sort={branchSort} numeric>
										Accounts
									</SortableTh>
									<SortableTh field="casaPct" sort={branchSort} numeric>
										CASA %
									</SortableTh>
									<SortableTh field="savingsCr" sort={branchSort} numeric>
										Savings
									</SortableTh>
									<SortableTh field="fdCr" sort={branchSort} numeric>
										FD
									</SortableTh>
									<SortableTh field="rdCr" sort={branchSort} numeric>
										RD
									</SortableTh>
									<SortableTh field="currentCr" sort={branchSort} numeric>
										Current
									</SortableTh>
								</tr>
							</thead>
							<tbody>
								{branchSort.rows.map((b) => (
									<tr
										key={`${b.orgId}-${b.branchIdLocal}`}
										className="clickable"
										onClick={() =>
											navigate(
												`/banks/${b.orgId}/branches/${encodeURIComponent(b.branchIdLocal)}`,
											)
										}
										tabIndex={0}
									>
										<td>
											<BankBadge orgId={b.orgId} orgName={b.orgName} />
										</td>
										<td style={{ fontVariantNumeric: 'tabular-nums' }}>
											{b.branchIdLocal}
										</td>
										<td>{b.branchName || '—'}</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatAbsCrore(b.depositCr)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatCount(b.depositCount)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatPct(b.casaPct)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatAbsCrore(b.savingsCr)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatAbsCrore(b.fdCr)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatAbsCrore(b.rdCr)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatAbsCrore(b.currentCr)}
										</td>
									</tr>
								))}
								{!branchSort.rows.length && (
									<tr>
										<td colSpan={10}>
											<EmptyState
												title="No branch data"
												hint="The selected banks have no branches in tblBranchSummary for this snapshot."
											/>
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</Card>
			)}
		</div>
	);
}
